<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import apiClient from '@src/api/api-client';
import { ItemInfoProps, useItemInfoComposable } from '@components/composables/item-info-composable';
import { ApiError } from '@src/api/api-error';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import ItemLoader from '@components/loader/item-loader.vue';
import Headline from '@components/headline.vue';
import Text from '@components/text.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import Card from '@components/card.vue';

const props = defineProps<ItemInfoProps & {
    idBin?: number,
}>();

const stockData = ref<Array<{ sku: string, qty: string, serial: string|null, binCode: string }>>([]);

const { loading: productLoading, product } = useItemInfoComposable();

const loading = ref<boolean>(false);
watch(productLoading, async () => {
    if (productLoading.value) {
        return;
    }

    loading.value = true;
    try {
        const request = apiClient.get('/be/stock/stock-on-bin/' + props.idBin + '/' + product.sku);
        request.setCustomErrorHttpCodes(409);
        stockData.value = (await request).data;
    } catch (e) {
        if (e instanceof ApiError && e.code === 'EMPTY_BIN') {
            return;
        }
        throw e;
    } finally {
        loading.value = false;
    }
}, { immediate: true });

const groupedStockData = computed(() => {
    const groupedData: { [key: string]: { quantity: number, serials: Array<string> } } = {};
    for (const entry of stockData.value) {
        if (!groupedData.hasOwnProperty(entry.sku)) {
            groupedData[entry.sku] = {
                quantity: 0,
                serials: [],
            };
        }
        groupedData[entry.sku].quantity += Number.parseInt(entry.qty);
        if (entry.serial === null) {
            return;
        }
        groupedData[entry.sku].serials.push(entry.serial);
    }

    return groupedData;
});
</script>

<template>
    <div v-if="!loading">
        <VerticalAlignChildrenWrapper v-if="stockData.length > 0" gap-bottom="2" avoid-outer-gap>
            <Card card-icon="box-open" card-color="green">
                <HorizontalAlignChildrenWrapper gap="1" center-children :wrap-on-small-screen="false">
                    <Text>Lagerfach:</Text>
                    <Headline>{{ stockData[0].binCode }}</Headline>
                </HorizontalAlignChildrenWrapper>
            </Card>
            <div v-for="entry in groupedStockData" :class="$style.group">
                <HorizontalAlignChildrenWrapper gap="1" :wrap-on-small-screen="false">
                    <Text gap="1">Anzahl:</Text>
                    <Text bold gap="1">{{ entry.quantity }} Stück</Text>
                </HorizontalAlignChildrenWrapper>
                <HorizontalAlignChildrenWrapper v-if="entry.serials.length > 0" gap="2" :wrap-on-small-screen="false" wrap :class="$style.serial__wrapper">
                    <p v-for="serial in entry.serials" :class="$style.serial">{{ serial }}</p>
                </HorizontalAlignChildrenWrapper>
            </div>
        </VerticalAlignChildrenWrapper>
        <Card v-else card-icon="info-circle" card-value="Es sind keine Artikel lagernd." card-color="blue"/>
    </div>
    <CenterChildrenWrapper v-else>
        <ItemLoader :static-loader="true"/>
    </CenterChildrenWrapper>
</template>

<style lang="scss" module>
@use '@text' as *;
@use '@borders' as *;

.group {
    padding-top: .5em;

    + .group {
        padding-top: 1.5em;
        border-top: $thin-line-grey;
    }
}
.serial__wrapper {
    margin-top: .5em;
}
.serial {
    padding: .5em;
    font-weight: $weight-medium;
    background-color: #fff;
    border: thin solid #8ba6c0;
    border-radius: 3px;
}
</style>
